/* eslint-disable @typescript-eslint/no-explicit-any */
// react
import { useCallback, useMemo, useState } from 'react';

// internationalization
import { useTranslation } from 'react-i18next';

// material ui
import { Box, useTheme } from '@mui/material';
import {
  DataGridPremium,
  GRID_TREE_DATA_GROUPING_FIELD,
  GridCellParams,
  GridColDef,
  GridSortModel,
  useGridApiRef,
} from '@mui/x-data-grid-premium';

// feature-utils
import { getAnalysisTypeIcon } from '@/features/energyxplorer/utils/analysis-type-helpers';

// components
import PortfolioAnalysisServiceCard from '@/features/energyxplorer/portfolio/components/datagrid/PortfolioAnalysisServiceCard';
import PortfolioAnalysisPresetTabs from '@/features/energyxplorer/portfolio/components/datagrid/type-datagrid/PortfolioAnalysisPresetTabs';
import usePortfolioDegreeDayCorrection from '@/features/energyxplorer/portfolio/hooks/usePortfolioDegreeDayCorrection';
import type { AnalysisType } from '@/features/energyxplorer/types';
import { useDatagridLocaleText } from '@/hooks/useDatagridLocaleText';
import { useLocalStorageJson } from '@/hooks/useLocalStorageJson';
import useSearchParams from '@/hooks/useStableSearchParams';

// relative
import CustomNoRowsOverlay from '../../../../../../components/ui/datagrid/DataGridNoRowsOverlay';
import { usePAColumns } from '../../../hooks/pa-columns';
import { getPresets } from '../../../utils/pa-presets';
import CustomColumnsPanel from './CustomColumnsPanel';
import PortfolioAnalysisToolbar from './PortfolioAnalysisToolbar';

interface Props {
  data: any;
  matrikelList: any[] | null | undefined;
  chosenPreset?: number;
  setSelectedMeter: (row: unknown) => void;
  setMeterType: (type: string) => void;
  analysisType: AnalysisType;
  isLoading: boolean;
}

const PortfolioAnalysisDataGrid = ({
  data,
  matrikelList,
  chosenPreset = 0,
  setSelectedMeter,
  setMeterType,
  analysisType,
  isLoading,
}: Props) => {
  const theme = useTheme();
  const { t } = useTranslation([
    `portfolio-analysis_${analysisType}`,
    'portfolio-analsysis_dashboard',
  ]);
  const localeText = useDatagridLocaleText();
  const { isDegreeDayCorrectionEnabled, setDegreeDayCorrection } =
    usePortfolioDegreeDayCorrection(analysisType);

  const [favorites, setFavorites] = useLocalStorageJson(
    'portfolio_analysis_favorites',
    {},
  );
  const [searchParams] = useSearchParams();
  const isFullscreen = searchParams.get('fullscreen') === 'true';
  const transformedData = useMemo(() => {
    return {
      ...data,
      portfolio_analysis_list: (data.portfolio_analysis_list as any[]).map(
        (item, index) => ({
          ...item,
          favorites: Boolean(favorites[item.matrikel_key]),
          id: `matrikel_${item.matrikel_key ?? index}`,
          average_weekday_night: item.average_weekday_night ?? null,
          inactive_active_ratio: item.inactive_active_ratio ?? null,
          meter_in_sync: item.meter_in_sync,
        }),
      ),
      summed_data: data.summed_data,
    };
  }, [data, favorites]);

  // Build flat rows with paths for tree data: matrikel parents + meter children
  const treeRows = useMemo(() => {
    const rows: any[] = (transformedData.portfolio_analysis_list as any[]).map(
      (parentRow) => ({
        ...parentRow,
        path: [parentRow.matrikel_key ?? parentRow.id],
      }),
    );

    if (!matrikelList) return rows;

    (transformedData.portfolio_analysis_list as any[]).forEach((parentRow) => {
      const matrikelItem = matrikelList.find(
        (m) => m.metadata.MatrikelKey === parentRow.matrikel_key,
      );
      if (!matrikelItem) return;

      const meters: any[] = matrikelItem[analysisType]?.meter_list ?? [];
      meters.forEach((entry, idx) => {
        const meterId =
          entry.metadata.UnitId ||
          entry.metadata.MeteringPointId ||
          entry.metadata.ReMoniUnitId ||
          `idx_${idx}`;
        rows.push({
          ...entry.analysis,
          ...entry.metadata,
          favorites: parentRow.favorites,
          id: `meter_${meterId}`,
          path: [parentRow.matrikel_key ?? parentRow.id, meterId],
          matrikel_key: parentRow.matrikel_key,
          main_address: parentRow.main_address,
          display_name:
            entry.metadata.DisplayName ||
            entry.metadata.Name ||
            entry.analysis?.display_name,
          type: analysisType,
          average_weekday_night: entry.analysis?.average_weekday_night ?? null,
          inactive_active_ratio: entry.analysis?.inactive_active_ratio ?? null,
        });
      });
    });

    return rows;
  }, [transformedData.portfolio_analysis_list, matrikelList, analysisType]);

  const getTreeDataPath = useCallback((row: any) => row.path as string[], []);

  // 2️⃣ single source of truth for toggling

  // cast to any so index access is safe throughout
  const presets: any = useMemo(
    () => getPresets(data, t, analysisType),
    [data, t, analysisType],
  );

  const apiRef = useGridApiRef();

  const totalColumns = usePAColumns(
    transformedData,
    analysisType,
    presets,
    chosenPreset,
  );

  // Derive columns during render
  const columns = useMemo(() => {
    const firstColumns = presets[chosenPreset].columnGroupingModel.map(
      (group: any) => {
        return group.children[0]?.field;
      },
    );
    firstColumns.push('warnings');
    const chosenOrder = presets[chosenPreset].order;
    if (chosenOrder.length > 0) {
      let tempArray: any[] = [];
      chosenOrder.forEach((field: any) => {
        if (field === '__row_group_by_columns_group__') return;
        const column = totalColumns.find((col) => col.field === field);
        if (column) {
          tempArray.push({
            ...column,
            cellClassName: firstColumns.includes(column.field)
              ? 'cell-left-border'
              : '',
          });
        }
      });
      tempArray = [
        ...tempArray.slice(0, 1),
        totalColumns.find((col) => col.field === 'ExtractedBuildingUse'),
        ...tempArray.slice(1),
      ];
      // Ensure address/matrikel columns are always present
      const mainAddressCol = totalColumns.find(
        (col) => col.field === 'main_address',
      );
      if (
        mainAddressCol &&
        !tempArray.find((c) => c?.field === 'main_address')
      ) {
        tempArray = [mainAddressCol, ...tempArray];
      }
      return tempArray.filter(Boolean);
    }
    return totalColumns;
  }, [chosenPreset, presets, totalColumns]);

  const [isFavoritesFiltered, setIsFavoritesFiltered] = useState(false);
  const [filterModel, setFilterModel] = useState(
    presets[chosenPreset].filterModel,
  );
  function generateVisibilityModel(
    order: string[],
    cols: GridColDef[],
  ): Record<string, boolean> {
    const columnVisibilityModel: Record<string, boolean> = {};
    cols.forEach((column) => {
      columnVisibilityModel[column.field] = false;
    });
    order.forEach((column) => {
      if (Object.prototype.hasOwnProperty.call(columnVisibilityModel, column)) {
        columnVisibilityModel[column] = true;
      }
    });
    columnVisibilityModel['ExtractedBuildingUse'] = false;
    columnVisibilityModel['address'] = true;
    return columnVisibilityModel;
  }
  const [columnVisibilityModel, setColumnVisibilityModel] = useState<
    Record<string, boolean>
  >(() =>
    generateVisibilityModel(
      presets[chosenPreset].order,
      totalColumns as GridColDef[],
    ),
  );

  // ✅ Reset state when preset changes (during render, no effect needed)
  const [prevChosenPreset, setPrevChosenPreset] = useState(chosenPreset);
  if (chosenPreset !== prevChosenPreset) {
    setPrevChosenPreset(chosenPreset);
    setFilterModel(presets[chosenPreset].filterModel);
    setColumnVisibilityModel(
      generateVisibilityModel(
        presets[chosenPreset].order,
        totalColumns as GridColDef[],
      ),
    );
  }

  // ✅ Derive final filter model by merging user filters with favorites filter
  const computedFilterModel = useMemo(() => {
    let items = [...filterModel.items];
    // Remove any existing favorites filter to avoid duplicates
    items = items.filter((item) => item.field !== 'favorites');

    if (isFavoritesFiltered) {
      items.push({
        id: 'favorites-filter',
        field: 'favorites',
        operator: 'is',
        value: true,
      });
    }
    return { ...filterModel, items };
  }, [filterModel, isFavoritesFiltered]);

  const initialState = {
    columns: {
      columnVisibilityModel: generateVisibilityModel(
        presets[chosenPreset].order,
        totalColumns as GridColDef[],
      ),
    },
    pinnedColumns: {
      left: [
        'favorites',
        'type',
        'linked_energy_groups',
        GRID_TREE_DATA_GROUPING_FIELD,
      ],
    },
    sorting: {
      sortModel: [...presets[chosenPreset].sortingModel],
    },
    filter: {
      filterModel: {
        items: [...presets[chosenPreset].filterModel.items],
        quickFilterExcludeHiddenColumns: false,
      },
    },
    density: 'compact' as const,
  };

  const handleCellClick = (params: GridCellParams) => {
    if (params.field === 'favorites') {
      // Only favorite matrikel (parent) rows
      if (params.rowNode.depth === 0) {
        setFavorites((prev: Record<string, unknown>) => ({
          ...prev,
          [params.row.matrikel_key]: !prev[params.row.matrikel_key],
        }));
      }
      return;
    }
    const isMeterChild = params.rowNode.depth > 0;
    setMeterType(isMeterChild ? 'meter' : 'matrikel');
    setSelectedMeter(params.row);
  };

  const handleFavoritesFilterClick = (bool: boolean) => {
    setIsFavoritesFiltered(bool);
  };

  const [isCollapsed, setIsCollapsed] = useState(true);

  const handleExpandCollapse = useCallback(() => {
    treeRows.forEach((row) => {
      if (row.path.length === 1) {
        apiRef.current?.setRowChildrenExpansion(row.id, isCollapsed);
      }
    });
    setIsCollapsed((prev) => !prev);
  }, [isCollapsed, treeRows, apiRef]);

  const analysisList = transformedData.portfolio_analysis_list as any[];
  const totalConsumptionYear = analysisList.reduce((acc: number, o) => {
    if (o.type_of_mp === 'PV') {
      return acc;
    }

    return o.total_consumption_year ? acc + o.total_consumption_year : acc;
  }, 0);
  const totalConsumptionLastYear = analysisList.reduce((acc: number, o) => {
    if (o.type_of_mp === 'PV') {
      return acc;
    }

    return o.total_consumption_last_year
      ? acc + o.total_consumption_last_year
      : acc;
  }, 0);
  const serviceCardProps = {
    label: t(`cards.${analysisType}.title`, {
      ns: 'portfolio-analysis_dashboard',
    }),
    value: totalConsumptionYear,
    trend:
      totalConsumptionLastYear > 0
        ? ((totalConsumptionYear - totalConsumptionLastYear) /
            totalConsumptionLastYear) *
          100
        : undefined,
    unit: t(`cards.${analysisType}.unit`, {
      ns: 'portfolio-analysis_dashboard',
    }),
    icon: getAnalysisTypeIcon(analysisType),
    color: (theme.palette as any)[analysisType].main,
    dataPct: (() => {
      const { sum, count } = analysisList.reduce(
        (acc: { sum: number; count: number }, o) => {
          const v = o.one_year_data_pct;
          if (Number.isFinite(v)) {
            acc.sum += v;
            acc.count += 1;
          }
          return acc;
        },
        { sum: 0, count: 0 },
      );
      return count ? sum / count : 0; // mean
    })(),
    area: analysisList.reduce(
      (acc: number, o) => (o.SumBuildingArea ? acc + o.SumBuildingArea : acc),
      0,
    ),
    buildingCount: new Set(analysisList.map((item) => item.main_address)).size,
    summedData: transformedData?.summed_data ?? [],
  };

  const [sortOverrides, setSortOverrides] = useState<
    Record<string, GridSortModel>
  >({});

  const sortModel =
    sortOverrides[chosenPreset] ?? presets[chosenPreset].sortingModel ?? [];

  const handleSortModelChange = (model: GridSortModel) => {
    setSortOverrides((current) => ({
      ...current,
      [chosenPreset]: model,
    }));
  };
  return (
    <>
      {!isFullscreen && (
        <Box mb={2} width={'100%'}>
          <PortfolioAnalysisServiceCard {...serviceCardProps} />
        </Box>
      )}

      <Box mb={2}>
        <PortfolioAnalysisPresetTabs analysisType={analysisType} />
      </Box>

      <DataGridPremium
        showToolbar
        localeText={localeText}
        slots={{
          toolbar: PortfolioAnalysisToolbar as any,
          columnsManagement: CustomColumnsPanel as any,
          noRowsOverlay: () => (
            <CustomNoRowsOverlay rows={t('noRows')} isLoading={isLoading} />
          ),
        }}
        slotProps={{
          toolbar: {
            showQuickFilter: true,
            handleFavoritesFilterClick: handleFavoritesFilterClick,
            isFavoritesFiltered: isFavoritesFiltered,
            setIsFavoritesFiltered: setIsFavoritesFiltered,
            handleExpandCollapse: handleExpandCollapse,
            isCollapsed: isCollapsed,
            showDegreeDayCorrectionToggle: analysisType === 'heat',
            isDegreeDayCorrectionEnabled,
            onDegreeDayCorrectionToggle: setDegreeDayCorrection,
          } as any,
          columnsManagement: {
            columnGroupingModel: presets[chosenPreset].columnGroupingModel,
          } as any,
          loadingOverlay: {
            variant: 'linear-progress',
            noRowsVariant: 'skeleton',
          },
        }}
        loading={isLoading}
        getRowClassName={(params) =>
          params.row.type_of_mp === 'PV' ? 'solar-row' : ''
        }
        treeData
        getTreeDataPath={getTreeDataPath}
        groupingColDef={{
          headerName: t('columns.main_address.label'),
          width: 350,
          valueGetter: (value, row) => row.display_name || row.address || value,
          sortable: true,
        }}
        rows={treeRows}
        columns={columns}
        showCellVerticalBorder={false}
        showColumnVerticalBorder={true}
        columnGroupingModel={presets[chosenPreset].columnGroupingModel}
        apiRef={apiRef}
        filterModel={computedFilterModel}
        onFilterModelChange={(newModel) => {
          const filteredItems = newModel.items.filter(
            (item) => item.field !== 'favorites',
          );
          setFilterModel({ ...newModel, items: filteredItems });
        }}
        columnVisibilityModel={columnVisibilityModel}
        onColumnVisibilityModelChange={(newModel) =>
          setColumnVisibilityModel(newModel)
        }
        initialState={initialState}
        sortModel={sortModel}
        onSortModelChange={handleSortModelChange}
        onCellClick={handleCellClick}
        columnHeaderHeight={70}
        columnGroupHeaderHeight={56}
        sortingOrder={['desc', 'asc', null]}
        sx={{
          '& .cell-left-border': {
            borderLeft: (theme) => `1px solid ${theme.palette.divider}`,
          },
          '& .MuiDataGrid-columnHeaderTitle': {
            fontWeight: 'bold',
          },
          '& .MuiDataGrid-columnHeaderTitleContainerContent': {
            height: '100%',
          },
          '&.MuiDataGrid-root .MuiDataGrid-columnHeader--alignRight .MuiDataGrid-columnHeaderTitleContainer':
            {
              pl: 1,
            },
          '& .solar-row .MuiDataGrid-cell': {
            color: '#00c853', // MUI green[600]
          },
          '& .MuiDataGrid-row': {
            cursor: 'pointer',
          },
        }}
      />
    </>
  );
};

export default PortfolioAnalysisDataGrid;
